import { createSelector } from 'reselect';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { type StoreState } from '../types/StoreState';
import { type ApplePayPaymentMethod } from '../types/PaymentMethods';
import { type AsyncStatus } from '../reducers/requestsState';
import { Experiment } from '../types/Experiment';
import { selectTopProps } from './topProps';
import { selectHighestSupportedApplePayVersion, selectApCanMakePayments, selectExperiments } from './appState';
import { selectRequestStateApCanMakePayments } from './requestsState';

const isFullfilledOrRejected = (s: AsyncStatus): boolean => {
  return s === 'fulfilled' || s === 'rejected';
};

export const selectApplePayDto = (state: StoreState): ApplePayPaymentMethod => {
  const apay = state.domain.paymentMethods.find((pm) => !!pm[PaymentMethod.ApplePay]);
  return apay as ApplePayPaymentMethod;
};

export const selectApplePayCountryCode = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod): string => pm && pm[PaymentMethod.ApplePay].merchantCountry,
);

export const selectApplePayMerchantCapabilities = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod) => pm && pm[PaymentMethod.ApplePay].merchantCapabilities,
);

export const selectApplePaySupportedNetworks = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod) => pm && pm[PaymentMethod.ApplePay].supportedNetworks,
);

export const selectApplePayBtnColor = createSelector(selectTopProps, selectExperiments, (topProps, experiments) => {
  // https://wix.atlassian.net/browse/PAY-13432
  if (experiments[Experiment.EnableApplePayInChrome] === 'true') {
    // For apple-pay-button web component (with SDK)
    return topProps.theme === 'light' ? 'white-outline' : 'black';
  }
  // For fallback button (without SDK)
  return topProps.theme === 'light' ? 'white' : 'black';
});

export const selectApplePayButtonAvailable = createSelector(
  selectApplePayDto,
  selectTopProps,
  selectHighestSupportedApplePayVersion,
  selectRequestStateApCanMakePayments,
  selectApCanMakePayments,
  (pm, topProps, highestSupportedVersion, requestStateCanMakePayments, canMakePayments) => {
    if (!topProps.allowPaymentMethods.includes(PaymentMethod.ApplePay)) {
      return false;
    }
    if (pm && topProps.demoMode) {
      return true;
    }
    if (highestSupportedVersion < 3) {
      // it's too old, not supported
      return false;
    }

    if (Number(topProps.paymentAmount) < 1) {
      // WixPayments min transaction is amount 1$
      return false;
    }
    if (!pm || !isFullfilledOrRejected(requestStateCanMakePayments)) {
      return false;
    }

    return canMakePayments;
  },
);
